import React from 'react';
import PropTypes from 'prop-types';
import { Play, FileText, Award } from 'react-feather';
import './CourseVideoTable.css';

/**
 * Shared Course Video Table Component
 * Modern horizontal table design displaying all videos and courses
 * Can be used by any user type (admin, school admin, teachers, etc.)
 */
const CourseVideoTable = ({
  videos,
  onSelectVideo,
  showCloseButton = false,
  onClose = null,
  isVideoUnlocked = null // Function to check if video is unlocked
}) => {
  if (!videos || videos.length === 0) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center py-5">
              <p className="text-muted">No videos available.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="course-video-table-container">
      <div className="course-table-header">
        <div className="course-table-header-content">
          <div className="course-table-header-title-col">
            <h2 className="course-table-title">All Videos and Courses</h2>
          </div>
          <div className="course-table-header-description-col">
            <p className="course-table-subtitle">
              Select a video to begin your training
            </p>
          </div>
          {showCloseButton && onClose && (
            <div className="course-table-header-button-col">
              <button
                className="course-table-header-btn"
                onClick={onClose}
              >
                Start Next Video
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="modern-course-table-wrapper">
        <table className="modern-course-table">
          <thead>
            <tr>
              <th className="series-col">
                <span className="th-content">
                  <Award size={16} />
                  <span>Series</span>
                </span>
              </th>
              <th className="module-col">Video/Module</th>
              <th className="description-col">Description</th>
              <th className="quizzes-col">
                <span className="th-content">
                  <FileText size={16} />
                  <span>Quizzes</span>
                </span>
              </th>
              <th className="action-col">Action</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((video, videoIndex) => {
              // Calculate series number: Each video gets its own sequential series number (1-8)
              const seriesNumber = Math.min(videoIndex + 1, 8);

              // Check if video is unlocked (if function provided)
              const isUnlocked = isVideoUnlocked ? isVideoUnlocked(videoIndex) : true;
              const isLocked = !isUnlocked;

              const description = video.description || "No description";
              const totalQuizzes = video.quizzes?.length || 0;

              // Get the first quiz's videoName for the module name, or use description
              const firstQuiz = video.quizzes && video.quizzes.length > 0 ? video.quizzes[0] : null;
              const videoModuleName = firstQuiz?.videoName || firstQuiz?.group || video.title || description;

              return (
                <tr
                  key={`video-${videoIndex}`}
                  className={`course-table-row ${isLocked ? 'course-row-locked' : ''}`}
                >
                  <td className="series-cell">
                    <div className={`series-badge ${isLocked ? 'series-badge-locked' : ''}`} title={isLocked ? `Series ${seriesNumber} - Locked (complete previous series first)` : `Series ${seriesNumber}`}>
                      {seriesNumber}
                    </div>
                  </td>
                  <td className="module-cell">
                    <div className={`module-name ${isLocked ? 'module-name-locked' : ''}`}>
                      {videoModuleName}
                      {isLocked && <span className="locked-indicator"> 🔒</span>}
                    </div>
                  </td>
                  <td className="description-cell">
                    <div className={`description-text ${isLocked ? 'description-text-locked' : ''}`}>
                      {description}
                    </div>
                  </td>
                  <td className="quizzes-cell">
                    <div className={`quizzes-badge ${isLocked ? 'quizzes-badge-locked' : ''}`}>
                      <FileText size={14} />
                      <span>{totalQuizzes} {totalQuizzes === 1 ? 'quiz' : 'quizzes'}</span>
                    </div>
                  </td>
                  <td className="action-cell">
                    {isLocked ? (
                      <button
                        className="modern-action-btn modern-action-btn-locked"
                        disabled
                        title={`Series ${seriesNumber} is locked. Complete Series ${seriesNumber - 1} first to unlock this module.`}
                      >
                        <Play size={14} />
                        Locked
                      </button>
                    ) : (
                      <button
                        className="modern-action-btn"
                        onClick={() => onSelectVideo(videoIndex, 0)}
                      >
                        <Play size={14} />
                        Video {videoIndex + 1}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

CourseVideoTable.propTypes = {
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      quizzes: PropTypes.arrayOf(
        PropTypes.shape({
          videoName: PropTypes.string,
          group: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  onSelectVideo: PropTypes.func.isRequired,
  showCloseButton: PropTypes.bool,
  onClose: PropTypes.func,
  isVideoUnlocked: PropTypes.func,
};

export default CourseVideoTable;
